import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Calendar, CheckCircle, Circle, FileText, Image, ExternalLink, Eye, Download, Paperclip, AlertCircle, Sparkles, Package, ClipboardList, ArrowUpDown, XCircle, MessageSquare, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { TaskWithDetails } from '@/services/taskService';
import ImageGallery from './ImageGallery';
import { taskCompletionService, TaskCompletion } from '@/firebase/taskCompletionService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formService, Form } from '@/firebase/formService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { agreementService, AgreementDocument } from '@/firebase/agreementService';
import { agreementCompletionService, AgreementCompletion } from '@/firebase/agreementCompletionService';
import { complianceService, ComplianceTemplate, ReferenceDocument } from '@/firebase/complianceService';
import { assetService, Asset } from '@/firebase/assetService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { reviewTask, rejectTask } from '@/services/taskService';
import { calculateDueDateFromExpiry, parseDocument, isGeminiConfigured, DocumentAnalysis } from '@/services/geminiService';
import { calculateNextDueDate } from '@/services/complianceTaskService';
import { useNotification } from '@/contexts/NotificationContext';
import { userService } from '@/firebase/database';
import { messagingIntegrationService } from '@/services/messagingIntegrationService';

interface ReviewTaskModalProps {
  task: TaskWithDetails;
  onReview: () => Promise<void>;
  onClose: () => void;
}

const ReviewTaskModal: React.FC<ReviewTaskModalProps> = ({ task, onReview, onClose }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [isReviewing, setIsReviewing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [taskConversationId, setTaskConversationId] = useState<string | null>(null);
  
  // Task completion data
  const [completion, setCompletion] = useState<TaskCompletion | null>(null);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(true);
  
  // AI Analysis states for auto-trigger
  const [isAnalyzingEvidence, setIsAnalyzingEvidence] = useState(false);
  const [pendingAnalyses, setPendingAnalyses] = useState<Set<string>>(new Set());
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  const hasTriggeredAnalysisRef = useRef<string | null>(null); // Track completion ID we've analyzed
  
  // Reference image viewer state
  const [viewingReferenceImageUrl, setViewingReferenceImageUrl] = useState<string | null>(null);
  const [viewingReferenceImageIndex, setViewingReferenceImageIndex] = useState<number>(0);
  const [viewingReferenceImageUrls, setViewingReferenceImageUrls] = useState<string[]>([]);
  
  // Keyboard navigation for reference image viewer
  useEffect(() => {
    if (!viewingReferenceImageUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setViewingReferenceImageUrl(null);
        setViewingReferenceImageUrls([]);
        setViewingReferenceImageIndex(0);
      } else if (e.key === 'ArrowLeft' && viewingReferenceImageIndex > 0) {
        const newIndex = viewingReferenceImageIndex - 1;
        setViewingReferenceImageIndex(newIndex);
        setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
      } else if (e.key === 'ArrowRight' && viewingReferenceImageIndex < viewingReferenceImageUrls.length - 1) {
        const newIndex = viewingReferenceImageIndex + 1;
        setViewingReferenceImageIndex(newIndex);
        setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingReferenceImageUrl, viewingReferenceImageIndex, viewingReferenceImageUrls]);
  
  // Checklist run history states
  const [checklistRuns, setChecklistRuns] = useState<ChecklistCompletion[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [templateChecklist, setTemplateChecklist] = useState<Checklist | null>(null);
  const [selectedRun, setSelectedRun] = useState<ChecklistCompletion | null>(null);
  
  // Form run history states
  const [formRuns, setFormRuns] = useState<FormCompletion[]>([]);
  const [isLoadingFormHistory, setIsLoadingFormHistory] = useState(false);
  const [templateForm, setTemplateForm] = useState<Form | null>(null);
  const [selectedFormRun, setSelectedFormRun] = useState<FormCompletion | null>(null);
  
  // Agreement run history states
  const [agreementRuns, setAgreementRuns] = useState<AgreementCompletion[]>([]);
  const [isLoadingAgreementHistory, setIsLoadingAgreementHistory] = useState(false);
  const [templateAgreements, setTemplateAgreements] = useState<AgreementDocument[]>([]);
  const [selectedAgreementRun, setSelectedAgreementRun] = useState<AgreementCompletion | null>(null);
  
  // Template and reference documents
  const [template, setTemplate] = useState<ComplianceTemplate | null>(null);
  const [referenceDocuments, setReferenceDocuments] = useState<ReferenceDocument[]>([]);
  const [suggestedDueDate, setSuggestedDueDate] = useState<Date | null>(null);
  const [manualOverrideDate, setManualOverrideDate] = useState<Date | null>(null);
  const [dueDateOverrideSource, setDueDateOverrideSource] = useState<'manual' | 'ai' | 'frequency' | null>(null);
  const [storedAiDueDate, setStoredAiDueDate] = useState<Date | null>(null);
  const [dueDateSelection, setDueDateSelection] = useState<'manual' | 'ai' | 'frequency'>('frequency');
  const [manualDueDateInput, setManualDueDateInput] = useState('');
  
  // Asset-related states
  const [relatedAssets, setRelatedAssets] = useState<Asset[]>([]);
  const [assetCompletions, setAssetCompletions] = useState<{
    assetId: string;
    assetName: string;
    checklistCompletions: ChecklistCompletion[];
    formCompletions: FormCompletion[];
  }[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetSortBy, setAssetSortBy] = useState<'name' | 'completions' | 'pending'>('name');
  const [assetSortDirection, setAssetSortDirection] = useState<'asc' | 'desc'>('asc');
  const allEvidenceTypes = task.evidenceTypes || [];
  const requiredEvidenceList = allEvidenceTypes.filter(e => e.required);
  const getEvidenceKey = (evidence: { type: string; label: string }, index: number) =>
    `${evidence.type || 'file'}_${index}`;

  // Prioritize AI suggested date when available, but respect manual overrides
  const resolvedDueDateOverrideSource: 'manual' | 'ai' | 'frequency' =
    dueDateOverrideSource === 'manual' ? 'manual' : (suggestedDueDate ? 'ai' : (dueDateOverrideSource || 'frequency'));

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Always show due date review if there's completion evidence or if reviewing a task
  const shouldShowDueDateReview = useMemo(() => {
    const hasCompletionEvidence = Boolean(
      completion?.evidenceUrls?.length ||
      (completion?.documentAnalyses && Object.keys(completion.documentAnalyses).length > 0) ||
      (completion?.assetEvidence && completion.assetEvidence.length > 0)
    );
    // Always show if there's completion evidence (even without AI date)
    // or if we're reviewing a task (completion exists)
    return hasCompletionEvidence || Boolean(completion);
  }, [completion?.documentAnalyses, completion?.assetEvidence, completion?.evidenceUrls, completion]);

  // Load conversation for task
  useEffect(() => {
    const loadTaskConversation = async () => {
      if (!task?.id || !currentOrganization?.id) {
        setTaskConversationId(null);
        return;
      }
      
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const conversationsRef = collection(db, 'conversations');
        const conversationsQuery = query(
          conversationsRef,
          where('linkedItems.taskId', '==', task.id)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);
        
        let foundConversationId: string | null = null;
        conversationsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.linkedItems?.taskId === task.id) {
            foundConversationId = doc.id;
          }
        });
        
        setTaskConversationId(foundConversationId);
      } catch (error: any) {
        // If index error, conversation might not exist yet
        if (error.code !== 'failed-precondition' && !error.message?.includes('index')) {
          console.error('Error loading task conversation:', error);
        }
        setTaskConversationId(null);
      }
    };
    
    loadTaskConversation();
  }, [task?.id, currentOrganization?.id]);

  useEffect(() => {
    // Reset state when task changes
    setCompletion(null);
    setIsLoadingCompletion(true);
    setSuggestedDueDate(null);
    setManualOverrideDate(null);
    setDueDateOverrideSource(null);
    setStoredAiDueDate(null);
    setDueDateSelection('frequency');
    setManualDueDateInput('');
    setIsAnalyzingEvidence(false);
    setPendingAnalyses(new Set());
    setAnalyzingFile(null);
    setChecklistRuns([]);
    setFormRuns([]);
    setAgreementRuns([]);
    setSelectedRun(null);
    setSelectedFormRun(null);
    setSelectedAgreementRun(null);
    setShowRejectInput(false);
    setRejectionReason('');
    
    // Load data for new task
    loadCompletionData();
    loadChecklistHistory();
    loadFormHistory();
    loadAgreementHistory();
    loadTemplateReferenceDocuments();
    loadRelatedAssets();
  }, [task.id, task.locationId, task.templateId]);

  useEffect(() => {
    if (shouldShowDueDateReview && resolvedDueDateOverrideSource === 'manual' && manualOverrideDate) {
      setDueDateSelection('manual');
      setManualDueDateInput(formatDateForInput(manualOverrideDate));
    } else if (shouldShowDueDateReview && suggestedDueDate) {
      // Prioritize AI suggested date when available
      setDueDateSelection('ai');
      setManualDueDateInput('');
    } else {
      setDueDateSelection('frequency');
      setManualDueDateInput('');
    }
  }, [resolvedDueDateOverrideSource, manualOverrideDate, suggestedDueDate, shouldShowDueDateReview]);

  // Helper to get date from various formats
  const getDateFromValue = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      try {
        return value.toDate();
      } catch {
        return null;
      }
    }
    if (typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  useEffect(() => {
    if (!shouldShowDueDateReview) {
      return;
    }

    if (dueDateSelection === 'manual' && !manualDueDateInput) {
      // Try to get default date from: manualOverrideDate -> suggestedDueDate -> task.dueDate
      const defaultDate = manualOverrideDate || suggestedDueDate || getDateFromValue(task.dueDate);
      if (defaultDate) {
        setManualDueDateInput(formatDateForInput(defaultDate));
      }
    }
  }, [dueDateSelection, manualDueDateInput, manualOverrideDate, suggestedDueDate, shouldShowDueDateReview, task.dueDate]);

  // Calculate next due date based on template frequency
  const calculatedFrequencyDueDate = useMemo(() => {
    const frequency = (task as any).frequency || task.templateSnapshot?.frequency || template?.frequency || 'P1Y';
    const baseDate = getDateFromValue(task.dueDate);
    if (!baseDate) return null;
    try {
      return calculateNextDueDate(baseDate, frequency);
    } catch (error) {
      console.error('Error calculating frequency-based due date:', error);
      return null;
    }
  }, [task.dueDate, (task as any).frequency, task.templateSnapshot?.frequency, template?.frequency]);

  // Format calculated frequency date for date input max attribute (YYYY-MM-DD)
  const maxDateInput = useMemo(() => {
    if (!calculatedFrequencyDueDate) return undefined;
    return formatDateForInput(calculatedFrequencyDueDate);
  }, [calculatedFrequencyDueDate]);

  const parseStoredDate = (value: any): Date | null => {
    if (!value && value !== 0) return null;
    if (value instanceof Date) return value;
    if (value && typeof value === 'object' && typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const loadCompletionData = async () => {
    setIsLoadingCompletion(true);
    try {
      const result = await taskCompletionService.getCompletionsForTask(task.id);
      if (result.success && result.data && result.data.length > 0) {
        const completionData = result.data[0];
        console.log('📊 Loaded completion data (raw):', JSON.stringify(completionData, null, 2));
        console.log('📊 All completion data keys:', Object.keys(completionData));
        console.log('📊 evidenceFileNames:', completionData.evidenceFileNames);
        console.log('📊 documentAnalyses:', completionData.documentAnalyses);
        console.log('📊 documentAnalyses exists?', 'documentAnalyses' in completionData);
        console.log('📊 documentAnalyses value:', completionData.documentAnalyses);
        console.log('📊 Raw documentAnalyses type:', typeof completionData.documentAnalyses);
        console.log('📊 documentAnalyses keys:', completionData.documentAnalyses ? Object.keys(completionData.documentAnalyses) : 'null/undefined');
        
        // Ensure evidenceFileNames is an array (it might be undefined)
        if (!completionData.evidenceFileNames && completionData.evidenceUrls) {
          completionData.evidenceFileNames = completionData.evidenceUrls.map((_, idx) => `Evidence File ${idx + 1}`);
        }

        // Parse documentAnalyses if it exists (convert ISO date strings back to Date objects)
        if (completionData.documentAnalyses && typeof completionData.documentAnalyses === 'object') {
          const parsedAnalyses: Record<string, any> = {};
          for (const [fileName, analysis] of Object.entries(completionData.documentAnalyses)) {
            if (analysis && typeof analysis === 'object') {
              parsedAnalyses[fileName] = {
                ...analysis,
                expiryDate: analysis.expiryDate ? new Date(analysis.expiryDate) : null,
                extractedDates: (analysis.extractedDates || []).map((d: any) => ({
                  ...d,
                  date: d.date ? new Date(d.date) : new Date(),
                })),
                // Ensure keyInformation is properly formatted (it might be an object or string)
                keyInformation: typeof analysis.keyInformation === 'object' && analysis.keyInformation !== null
                  ? analysis.keyInformation
                  : {},
              };
            }
          }
          completionData.documentAnalyses = parsedAnalyses;
          console.log('📊 Parsed documentAnalyses:', parsedAnalyses);
        }
        
        // If completedBy looks like a user ID (not already formatted), fetch user details
        if (completionData.completedBy && typeof completionData.completedBy === 'string' && 
            completionData.completedBy.length > 20 && /^[a-zA-Z0-9]+$/.test(completionData.completedBy) && 
            !completionData.completedBy.includes('@') && !completionData.completedBy.includes('(')) {
          try {
            const userResult = await userService.getUser(completionData.completedBy);
            if (userResult.success && userResult.data) {
              const userName = userResult.data.name || userResult.data.displayName || userResult.data.email || '';
              const userEmail = userResult.data.email || '';
              if (userEmail) {
                completionData.completedBy = `${userName} (${userEmail})`;
              } else {
                completionData.completedBy = userName || completionData.completedBy;
              }
            }
          } catch (error) {
            console.error(`Error fetching user ${completionData.completedBy}:`, error);
          }
        }
        
        setCompletion(completionData); // Get the most recent completion
        setDueDateOverrideSource((completionData.dueDateOverrideSource as any) || null);
        setManualOverrideDate(parseStoredDate(completionData.manualNextDueDate));
        const storedAiDue = parseStoredDate(completionData.aiSuggestedDueDate);
        setStoredAiDueDate(storedAiDue);

        // Compute AI suggested due date, if expiry found in any analysis
        try {
          const analyses = completionData.documentAnalyses || {};
          const expiryDates: Date[] = [];
          Object.values(analyses).forEach((analysis: any) => {
            const exp = analysis?.expiryDate;
            let d: Date | null = null;
            if (exp?.toDate) {
              d = exp.toDate();
            } else if (typeof exp === 'string') {
              const parsed = new Date(exp);
              d = isNaN(parsed.getTime()) ? null : parsed;
            } else if (exp instanceof Date) {
              d = exp;
            }
            if (d && !isNaN(d.getTime())) {
              expiryDates.push(d);
            }
          });

          if (storedAiDue) {
            setSuggestedDueDate(storedAiDue);
          } else if (expiryDates.length > 0) {
            // Use the earliest expiry date among analyses
            const earliest = expiryDates.reduce((a, b) => (a.getTime() < b.getTime() ? a : b));
            const leadTimeDays = task.leadTimeDays || 30;
            const due = calculateDueDateFromExpiry(earliest, leadTimeDays);
            setSuggestedDueDate(due);
          } else {
            setSuggestedDueDate(null);
          }
        } catch (e) {
          console.warn('Could not compute AI suggested due date in review modal:', e);
          setSuggestedDueDate(null);
        }
      }
    } catch (error) {
      console.error('Error loading completion data:', error);
    } finally {
      setIsLoadingCompletion(false);
    }
  };

  // Auto-trigger AI analysis if missing when completion data is loaded
  useEffect(() => {
    // Don't trigger if already loading or analyzing
    if (isLoadingCompletion || isAnalyzingEvidence) return;
    if (!completion?.id) {
      // Reset ref when completion is cleared
      hasTriggeredAnalysisRef.current = null;
      return;
    }
    if (!isGeminiConfigured()) return;

    // Prevent re-triggering if we've already analyzed this completion
    if (hasTriggeredAnalysisRef.current === completion.id) {
      return;
    }

    // Check if there are evidence files but no/missing documentAnalyses
    const evidenceUrls = completion.evidenceUrls || [];
    const evidenceFileNames = completion.evidenceFileNames || [];
    const documentAnalyses = completion.documentAnalyses || {};

    if (evidenceUrls.length === 0) {
      // No files, mark as processed
      hasTriggeredAnalysisRef.current = completion.id;
      return;
    }

    // Find files that need analysis (have URL but no analysis)
    const filesNeedingAnalysis: { url: string; fileName: string; index: number }[] = [];
    evidenceUrls.forEach((url, idx) => {
      if (url.startsWith('UPLOAD_FAILED:')) return; // Skip failed uploads
      
      const fileName = evidenceFileNames[idx] || `Evidence File ${idx + 1}`;
      // Skip if analysis already exists for this file
      if (documentAnalyses[fileName]) {
        return;
      }
      
      // Add to list - we'll check file type when fetching
      filesNeedingAnalysis.push({ url, fileName, index: idx });
    });

    // If there are files needing analysis, trigger analysis
    if (filesNeedingAnalysis.length > 0) {
      console.log('🔍 Auto-triggering AI analysis for', filesNeedingAnalysis.length, 'file(s)');
      hasTriggeredAnalysisRef.current = completion.id; // Mark that we've triggered for this completion
      triggerAnalysisForMissingFiles(filesNeedingAnalysis);
    } else {
      // No files need analysis, mark as processed
      hasTriggeredAnalysisRef.current = completion.id;
    }
  }, [completion, isLoadingCompletion, isAnalyzingEvidence]);

  const triggerAnalysisForMissingFiles = async (filesToAnalyze: { url: string; fileName: string; index: number }[]) => {
    if (!completion?.id || filesToAnalyze.length === 0) return;
    
    // Store the original completion documentAnalyses to check what was newly analyzed
    const originalAnalyses = completion.documentAnalyses || {};

    setIsAnalyzingEvidence(true);
    const pendingSet = new Set<string>();
    filesToAnalyze.forEach(f => pendingSet.add(f.fileName));
    setPendingAnalyses(pendingSet);

    try {
      const newAnalyses: Record<string, DocumentAnalysis> = completion.documentAnalyses ? { ...completion.documentAnalyses } : {};
      let suggestedDueDateFromAnalysis: Date | null = null;

      // Analyze each file
      for (const { url, fileName } of filesToAnalyze) {
        setAnalyzingFile(fileName);
        
        try {
          // Fetch the file from URL and convert to File object
          const response = await fetch(url);
          if (!response.ok) {
            console.error(`Failed to fetch file ${fileName} from ${url}`);
            // Mark as analyzed with error message so it doesn't retry
            newAnalyses[fileName] = {
              summary: 'Cannot analyze this file: Failed to fetch file',
              expiryDate: null,
              extractedDates: [],
              documentType: 'unknown',
              keyInformation: {},
            };
            pendingSet.delete(fileName);
            setPendingAnalyses(new Set(pendingSet));
            continue;
          }

          const blob = await response.blob();
          // Determine file type from URL or blob
          const contentType = response.headers.get('content-type') || blob.type;
          const isImage = contentType.startsWith('image/');
          const isPDF = contentType === 'application/pdf' || url.toLowerCase().endsWith('.pdf');

          if (!isImage && !isPDF) {
            console.warn(`File ${fileName} is not an image or PDF, marking as non-analyzable`);
            // Mark as analyzed with message so it doesn't retry
            newAnalyses[fileName] = {
              summary: 'Cannot analyze this file: File type not supported (only images and PDFs can be analyzed)',
              expiryDate: null,
              extractedDates: [],
              documentType: 'unsupported',
              keyInformation: {},
            };
            pendingSet.delete(fileName);
            setPendingAnalyses(new Set(pendingSet));
            continue;
          }

          // Create a File object from the blob
          const file = new File([blob], fileName, { type: contentType });

          // Analyze the document
          const analysis = await parseDocument(file);
          newAnalyses[fileName] = analysis;

          // Check if this analysis has an expiry date for suggested due date
          if (analysis.expiryDate && !suggestedDueDateFromAnalysis) {
            const leadTimeDays = task.leadTimeDays || 30;
            suggestedDueDateFromAnalysis = calculateDueDateFromExpiry(analysis.expiryDate, leadTimeDays);
          }

          console.log(`✅ Analyzed ${fileName}:`, analysis.summary.substring(0, 50));
        } catch (error) {
          console.error(`Error analyzing ${fileName}:`, error);
          // Store error in analysis so it doesn't retry
          newAnalyses[fileName] = {
            summary: `Cannot analyze this file: ${error instanceof Error ? error.message : 'Unknown error'}`,
            expiryDate: null,
            extractedDates: [],
            documentType: 'unknown',
            keyInformation: {},
          };
        } finally {
          pendingSet.delete(fileName);
          setPendingAnalyses(new Set(pendingSet));
        }
      }

      // Save analyses back to completion document (including files that couldn't be analyzed)
      // We save if we processed any files, even if some couldn't be analyzed
      if (filesToAnalyze.length > 0 && completion.id) {
        // Merge with existing analyses (preserve any that already existed)
        const allAnalyses = { ...(completion.documentAnalyses || {}), ...newAnalyses };
        
        try {
          // Convert Date objects to ISO strings for Firestore
          const analysesForStorage: Record<string, any> = {};
          Object.entries(allAnalyses).forEach(([fileName, analysis]) => {
            analysesForStorage[fileName] = {
              ...analysis,
              expiryDate: analysis.expiryDate ? analysis.expiryDate.toISOString() : null,
              extractedDates: (analysis.extractedDates || []).map((d: any) => ({
                ...d,
                date: d.date instanceof Date ? d.date.toISOString() : d.date,
              })),
            };
          });

          // Also ensure evidenceFileNames is saved if it doesn't exist
          const updates: any = {
            documentAnalyses: analysesForStorage,
          };
          
          // If evidenceFileNames is missing, save it using the file names we generated
          if (!completion.evidenceFileNames || completion.evidenceFileNames.length === 0) {
            const fileNames: string[] = [];
            (completion.evidenceUrls || []).forEach((_, idx) => {
              const fileName = filesToAnalyze.find(f => f.index === idx)?.fileName || `Evidence File ${idx + 1}`;
              fileNames.push(fileName);
            });
            if (fileNames.length > 0) {
              updates.evidenceFileNames = fileNames;
            }
          }
          
          if (suggestedDueDateFromAnalysis) {
            updates.aiSuggestedDueDate = suggestedDueDateFromAnalysis.toISOString();
          }
          
          // Update completion document with analyses
          await taskCompletionService.updateCompletion(completion.id, updates);

          // Reload completion data to ensure proper parsing and consistency
          await loadCompletionData();

          // Update suggested due date if we got one from analysis
          if (suggestedDueDateFromAnalysis) {
            setSuggestedDueDate(suggestedDueDateFromAnalysis);
            setDueDateSelection('ai');
          }

          console.log('✅ Saved analyses to completion document');
          // Only show notification if we actually analyzed new files (not just marked as non-analyzable)
          const successfullyAnalyzed = filesToAnalyze.filter(f => {
            const analysis = newAnalyses[f.fileName];
            return analysis && 
                   !analysis.summary?.startsWith('Cannot analyze') && 
                   !analysis.summary?.startsWith('Analysis failed');
          }).length;
          
          if (successfullyAnalyzed > 0) {
            showNotification(`AI analysis completed for ${successfullyAnalyzed} file(s)`, 'success');
          }
        } catch (error) {
          console.error('Error saving analyses to completion:', error);
          showNotification('Analysis completed but failed to save results', 'error');
        }
      }
    } catch (error) {
      console.error('Error in auto-analysis:', error);
      showNotification('Error running AI analysis', 'error');
    } finally {
      setIsAnalyzingEvidence(false);
      setAnalyzingFile(null);
      setPendingAnalyses(new Set());
    }
  };

  const loadTemplateReferenceDocuments = async () => {
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading template reference documents');
        return;
      }
      
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const foundTemplate = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (foundTemplate) {
          setTemplate(foundTemplate);
          setReferenceDocuments(foundTemplate.referenceDocuments || []);
        } else {
          console.warn('⚠️ Template not found:', { templateId: task.templateId, templateName: task.templateName });
        }
      }
    } catch (error) {
      console.error('Error loading template reference documents:', error);
    }
  };

  const handleViewCompletion = async (completion: ChecklistCompletion | FormCompletion, type: 'checklist' | 'form') => {
    if (type === 'checklist') {
      const checklistComp = completion as ChecklistCompletion;
      setSelectedRun(checklistComp);
      // Load the checklist template, but only if it matches the task's template
      if (checklistComp.checklistId) {
        const checklistResult = await checklistService.getChecklist(checklistComp.checklistId);
        if (checklistResult.success && checklistResult.data) {
          // Verify the checklist matches the task's template before displaying it
          if (task.templateId && checklistResult.data.templateId === task.templateId) {
            setTemplateChecklist(checklistResult.data);
          } else {
            console.log(`[ReviewTaskModal] Not displaying checklist "${checklistResult.data.name}" - templateId mismatch`, {
              checklistTemplateId: checklistResult.data.templateId,
              taskTemplateId: task.templateId
            });
            // Don't set templateChecklist if it doesn't match - just show the completion details
          }
        }
      }
    } else {
      const formComp = completion as FormCompletion;
      setSelectedFormRun(formComp);
      // Load the form template, but only if it matches the task's template
      if (formComp.formId) {
        const formResult = await formService.getForm(formComp.formId);
        if (formResult.success && formResult.data) {
          // Verify the form matches the task's template before displaying it
          if (task.templateId && formResult.data.templateId === task.templateId) {
            setTemplateForm(formResult.data);
          } else {
            console.log(`[ReviewTaskModal] Not displaying form "${formResult.data.name}" - templateId mismatch`, {
              formTemplateId: formResult.data.templateId,
              taskTemplateId: task.templateId
            });
            // Don't set templateForm if it doesn't match - just show the completion details
          }
        }
      }
    }
  };

  const loadRelatedAssets = async () => {
    if (!currentOrganization?.id || !task.locationId || !task.templateId) {
      return;
    }
    
    setIsLoadingAssets(true);
    try {
      // Get all assets for the task's location
      const locationAssetsResult = await assetService.getAssetsByLocation(task.locationId, currentOrganization.id);
      if (!locationAssetsResult.success || !locationAssetsResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Get all asset types to check compliance template associations
      const assetTypesResult = await assetService.getAllAssetTypes(currentOrganization.id);
      if (!assetTypesResult.success || !assetTypesResult.data) {
        setRelatedAssets([]);
        return;
      }

      // Filter assets whose assetType has this task's templateId in complianceTemplateIds
      const assetTypesMap = new Map(assetTypesResult.data.map(at => [at.id!, at]));
      const relatedAssetsList = locationAssetsResult.data.filter(asset => {
        const assetType = assetTypesMap.get(asset.assetTypeId);
        return assetType?.complianceTemplateIds?.includes(task.templateId);
      });

      // Sort assets by name initially
      relatedAssetsList.sort((a, b) => {
        const nameA = (a.assetName || '').toLowerCase();
        const nameB = (b.assetName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setRelatedAssets(relatedAssetsList);

      // OPTIMIZATION: Load completions ONCE for the location, not per asset
      const getDateFromValue = (value: any): Date => {
        if (value instanceof Date) return value;
        if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
          return value.toDate();
        }
        if (typeof value === 'number') return new Date(value);
        return new Date(value);
      };

      // Normalize start date to beginning of day (00:00:00.000)
      const taskCreatedDate = (() => {
        const date = getDateFromValue(task.createdAt);
        date.setHours(0, 0, 0, 0);
        return date;
      })();

      // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
      // Normalize end date to end of day (23:59:59.999) to include all completions on that day
      const taskEndDate = (() => {
        const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
        const date = getDateFromValue(endDateValue);
        date.setHours(23, 59, 59, 999);
        return date;
      })();

      // Load all completions once, along with checklists and forms to filter by template
      const [allChecklistCompletionsResult, allFormCompletionsResult, checklistsResult, formsResult] = await Promise.all([
        checklistCompletionService.getCompletionsByLocation(task.locationId),
        formCompletionService.getCompletionsByLocation(task.locationId),
        checklistService.getAllChecklists(currentOrganization.id),
        formService.getAllForms(currentOrganization.id)
      ]);

      const allChecklistCompletions = allChecklistCompletionsResult.success && allChecklistCompletionsResult.data 
        ? allChecklistCompletionsResult.data 
        : [];
      const allFormCompletions = allFormCompletionsResult?.data || [];
      
      // Create maps of checklist/form IDs to their templateIds for filtering
      const checklistTemplateMap = new Map<string, string>();
      if (checklistsResult.success && checklistsResult.data) {
        checklistsResult.data.forEach(checklist => {
          if (checklist.id && checklist.templateId) {
            checklistTemplateMap.set(checklist.id, checklist.templateId);
          }
        });
      }
      
      const formTemplateMap = new Map<string, string>();
      if (formsResult.success && formsResult.data) {
        formsResult.data.forEach(form => {
          if (form.id && form.templateId) {
            formTemplateMap.set(form.id, form.templateId);
          }
        });
      }

      // Filter completions by date range AND template ID
      const dateFilteredChecklistCompletions = allChecklistCompletions.filter(cc => {
        // First filter by date range - only include completions within task period
        const completionDate = cc.completedAt?.toDate ? cc.completedAt.toDate() :
          (typeof cc.completedAt === 'number' ? new Date(cc.completedAt) :
          (cc.completedAt instanceof Date ? cc.completedAt : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from checklists that match the task's template
        if (cc.checklistId) {
          const checklistTemplateId = checklistTemplateMap.get(cc.checklistId);
          if (checklistTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[ReviewTaskModal] Excluding checklist completion for asset - template mismatch`, {
              checklistId: cc.checklistId,
              checklistTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no checklistId, exclude it
        return false;
      });

      const dateFilteredFormCompletions = allFormCompletions.filter(fc => {
        // First filter by date range - only include completions within task period
        const completionDate = fc.completionDate?.toDate ? fc.completionDate.toDate() :
          (typeof fc.completionDate === 'number' ? new Date(fc.completionDate) :
          (fc.completionDate instanceof Date ? fc.completionDate : new Date()));
        
        // Exclude completions before task creation or after task end (completedAt or dueDate)
        if (completionDate < taskCreatedDate || completionDate > taskEndDate) {
          return false;
        }
        
        // Then filter by template ID - only include completions from forms that match the task's template
        if (fc.formId) {
          const formTemplateId = formTemplateMap.get(fc.formId);
          if (formTemplateId === task.templateId) {
            return true;
          } else {
            console.log(`[ReviewTaskModal] Excluding form completion for asset - template mismatch`, {
              formId: fc.formId,
              formTemplateId,
              taskTemplateId: task.templateId
            });
            return false;
          }
        }
        
        // If no formId, exclude it
        return false;
      });

      // Create asset ID set for quick lookup
      const assetIds = new Set(relatedAssetsList.filter(a => a.id).map(a => a.id!));

      // Group completions by asset ID
      const completionsByAssetId = new Map<string, {
        checklistCompletions: ChecklistCompletion[];
        formCompletions: FormCompletion[];
      }>();

      // Initialize all assets with empty completions
      relatedAssetsList.forEach(asset => {
        if (asset.id) {
          completionsByAssetId.set(asset.id, {
            checklistCompletions: [],
            formCompletions: []
          });
        }
      });

      // Distribute checklist completions to assets
      dateFilteredChecklistCompletions.forEach(cc => {
        const assetId = (cc as any).assetId || 
                       (cc.variableValues && cc.variableValues['assetId']) ||
                       (cc.variableValues && cc.variableValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.checklistCompletions.push(cc);
          }
        }
      });

      // Distribute form completions to assets
      dateFilteredFormCompletions.forEach(fc => {
        const assetId = (fc as any).assetId ||
                       (fc.fieldValues && fc.fieldValues['assetId']) ||
                       (fc.fieldValues && fc.fieldValues['asset']);
        if (assetId && assetIds.has(assetId)) {
          const existing = completionsByAssetId.get(assetId);
          if (existing) {
            existing.formCompletions.push(fc);
          }
        }
      });

      // Build completions data array
      const completionsData = relatedAssetsList
        .filter(asset => asset.id)
        .map(asset => {
          const completions = completionsByAssetId.get(asset.id!) || {
            checklistCompletions: [],
            formCompletions: []
          };
          return {
            assetId: asset.id!,
            assetName: asset.assetName,
            checklistCompletions: completions.checklistCompletions,
            formCompletions: completions.formCompletions
          };
        });

      setAssetCompletions(completionsData);
    } catch (error) {
      console.error('Error loading related assets:', error);
      setRelatedAssets([]);
      setAssetCompletions([]);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const loadFormHistory = async () => {
    setIsLoadingFormHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading form history');
        setIsLoadingFormHistory(false);
        return;
      }
      
      // Get the template ID from the task
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Find forms associated with this template
          const formsResult = await formService.getAllForms();
          if (formsResult.success && formsResult.data) {
            const associatedForm = formsResult.data.find(f => f.templateId === template.id);
            
            if (associatedForm) {
              setTemplateForm(associatedForm);
              
              // Get all form completion history for this form
              const historyResult = await formCompletionService.getCompletionsForForm(associatedForm.id!);
              if (historyResult.success && historyResult.data) {
                // Filter form runs by the task's location and date range (createdAt to dueDate)
                const getDateFromValue = (value: any): Date => {
                  if (value instanceof Date) {
                    return value;
                  }
                  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                    return value.toDate();
                  }
                  if (typeof value === 'number') {
                    return new Date(value);
                  }
                  return new Date(value);
                };
                
                // Normalize start date to beginning of day
                const taskCreatedDate = (() => {
                  const date = getDateFromValue(task.createdAt);
                  date.setHours(0, 0, 0, 0);
                  return date;
                })();
                
                // For completed tasks, use completedAt as end date; otherwise use dueDate
                // Normalize end date to end of day
                const taskEndDate = (() => {
                  const endDateValue = task.completedAt || task.dueDate;
                  const date = getDateFromValue(endDateValue);
                  date.setHours(23, 59, 59, 999);
                  return date;
                })();
                
                const filteredRuns = historyResult.data.filter(run => {
                  // Filter by location
                  if (run.locationId !== task.locationId) {
                    return false;
                  }
                  
                  // Filter by date range - only show runs within task period
                  let runDate: Date | null = null;
                  if (run.completionDate?.toDate) {
                    runDate = run.completionDate.toDate();
                  } else if (typeof run.completionDate === 'number') {
                    runDate = new Date(run.completionDate);
                  } else if (run.completionDate instanceof Date) {
                    runDate = run.completionDate;
                  } else if (run.completionDate) {
                    runDate = new Date(run.completionDate);
                  }
                  
                  if (!runDate) {
                    return false; // Skip runs without valid dates
                  }
                  
                  // Run date must be within task period (createdAt to completedAt/dueDate)
                  return runDate >= taskCreatedDate && runDate <= taskEndDate;
                });
                
                setFormRuns(filteredRuns);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading form history:', error);
    } finally {
      setIsLoadingFormHistory(false);
    }
  };

  const loadAgreementHistory = async () => {
    setIsLoadingAgreementHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading agreement history');
        setIsLoadingAgreementHistory(false);
        return;
      }
      
      // Get the template ID from the task
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Find agreements associated with this template
          const agreementsResult = await agreementService.getAllDocuments(currentOrganization?.id);
          if (agreementsResult.success && agreementsResult.data) {
            const associatedAgreements = agreementsResult.data.filter(a => a.templateId === template.id);
            
            console.log('🔍 Agreement History Debug (Review):', {
              templateId: template.id,
              templateName: template.name,
              taskLocationId: task.locationId,
              taskLocationName: task.locationName,
              foundAgreements: associatedAgreements.length,
              agreementIds: associatedAgreements.map(a => a.id)
            });
            
            if (associatedAgreements.length > 0) {
              setTemplateAgreements(associatedAgreements);
              
              // Get all agreement completions for these agreements
              const allCompletions: AgreementCompletion[] = [];
              
              for (const agreement of associatedAgreements) {
                if (agreement.id) {
                  const historyResult = await agreementCompletionService.getCompletionsForAgreement(agreement.id);
                  if (historyResult.success && historyResult.data) {
                    allCompletions.push(...historyResult.data);
                  }
                }
              }
              
              console.log('🔍 All completions before filtering (Review):', {
                totalCompletions: allCompletions.length,
                completionLocations: allCompletions.map(c => ({ id: c.locationId, agreementId: c.agreementId }))
              });
              
              // Filter agreement runs by the task's location and date range (createdAt to dueDate)
              const getDateFromValue = (value: any): Date => {
                if (value instanceof Date) {
                  return value;
                }
                if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                  return value.toDate();
                }
                if (typeof value === 'number') {
                  return new Date(value);
                }
                if (value && typeof value === 'object' && ('_seconds' in value || 'seconds' in value)) {
                  const seconds = value._seconds || value.seconds || 0;
                  const nanoseconds = value._nanoseconds || value.nanoseconds || 0;
                  return new Date(seconds * 1000 + nanoseconds / 1000000);
                }
                return new Date(value);
              };
              
              // Normalize start date to beginning of day
              const taskCreatedDate = (() => {
                const date = getDateFromValue(task.createdAt);
                date.setHours(0, 0, 0, 0);
                return date;
              })();
              
              // For completed tasks, use completedAt/completedDate as end date; otherwise use dueDate
              // Normalize end date to end of day
              const taskEndDate = (() => {
                const endDateValue = (task as any).completedAt || (task as any).completedDate || task.dueDate;
                const date = getDateFromValue(endDateValue);
                date.setHours(23, 59, 59, 999);
                return date;
              })();
              
              console.log('🔍 Date range (Review):', {
                taskCreatedDate: taskCreatedDate.toISOString(),
                taskEndDate: taskEndDate.toISOString(),
                usingCompletedAt: !!(task as any).completedAt || !!(task as any).completedDate
              });
              
              // Filter by location - prefer matching location, but show all if none match
              const locationFilteredRuns = allCompletions.filter(run => {
                // Compare locationId (case-insensitive string comparison)
                const runLocationId = run.locationId ? String(run.locationId).trim() : '';
                const taskLocationId = task.locationId ? String(task.locationId).trim() : '';
                
                if (runLocationId && taskLocationId) {
                  return runLocationId === taskLocationId;
                }
                
                // If no location IDs match, show all completions (more lenient)
                // This handles cases where location might not be set or there's a mismatch
                if (!runLocationId || !taskLocationId) {
                  return true;
                }
                
                // If location IDs don't match, still show them but log for debugging
                if (runLocationId !== taskLocationId) {
                  console.log('⚠️ Location ID mismatch (Review), showing anyway:', {
                    runLocationId,
                    taskLocationId,
                    run: { id: run.id, agreementId: run.agreementId, signerName: run.signerName }
                  });
                  // Still show the agreement even if location doesn't match
                  return true;
                }
                
                return true;
              });
              
              console.log('🔍 After location filtering (Review):', {
                locationFilteredCount: locationFilteredRuns.length,
                totalCompletions: allCompletions.length
              });
              
              // Filter by date range - show all recent agreements, be lenient with date range
              const filteredRuns = locationFilteredRuns.filter(run => {
                let runDate: Date | null = null;
                
                // Try to get date from signedAt first
                if (run.signedAt) {
                  try {
                    // Handle Firestore Timestamp explicitly
                    if (run.signedAt && typeof run.signedAt === 'object') {
                      // Check if it's a Firestore Timestamp with toDate
                      if ('toDate' in run.signedAt && typeof run.signedAt.toDate === 'function') {
                        runDate = run.signedAt.toDate();
                      }
                      // Check if it has _seconds property (Timestamp format)
                      else if ('_seconds' in run.signedAt || 'seconds' in run.signedAt) {
                        const seconds = run.signedAt._seconds || run.signedAt.seconds || 0;
                        const nanoseconds = run.signedAt._nanoseconds || run.signedAt.nanoseconds || 0;
                        runDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                      }
                      // Check if it's a serverTimestamp placeholder
                      else if (run.signedAt._methodName === 'serverTimestamp') {
                        // Use clientSignedAt as fallback
                        if (run.clientSignedAt) {
                          runDate = new Date(run.clientSignedAt);
                        } else {
                          console.log('⚠️ serverTimestamp placeholder with no clientSignedAt for run (Review):', run.id);
                          return false;
                        }
                      } else {
                        // Try generic date conversion
                        runDate = getDateFromValue(run.signedAt);
                      }
                    } else {
                      runDate = getDateFromValue(run.signedAt);
                    }
                  } catch (e) {
                    console.log('⚠️ Error parsing signedAt for run (Review):', run.id, e);
                  }
                }
                
                // Fallback to clientSignedAt if signedAt didn't work
                if ((!runDate || isNaN(runDate.getTime())) && run.clientSignedAt) {
                  runDate = new Date(run.clientSignedAt);
                }
                
                if (!runDate || isNaN(runDate.getTime())) {
                  console.log('⚠️ Invalid date for run (Review):', run.id, {
                    signedAt: run.signedAt,
                    signedAtType: typeof run.signedAt,
                    signedAtKeys: run.signedAt && typeof run.signedAt === 'object' ? Object.keys(run.signedAt) : 'N/A',
                    clientSignedAt: run.clientSignedAt
                  });
                  return false; // Skip runs without valid dates
                }
                
                // Be lenient with date range - show agreements within the task date range OR recent ones (within last 6 months)
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                
                const isInTaskRange = runDate >= taskCreatedDate && runDate <= taskEndDate;
                const isRecent = runDate >= sixMonthsAgo;
                
                // Show if in task range OR if recent (within last 6 months)
                const shouldShow = isInTaskRange || isRecent;
                
                if (!shouldShow) {
                  console.log('⚠️ Date out of range (Review):', {
                    runDate: runDate.toISOString(),
                    taskCreatedDate: taskCreatedDate.toISOString(),
                    taskEndDate: taskEndDate.toISOString(),
                    sixMonthsAgo: sixMonthsAgo.toISOString(),
                    runId: run.id
                  });
                }
                
                return shouldShow;
              });
              
              console.log('🔍 Final filtered runs (Review):', {
                finalCount: filteredRuns.length,
                runs: filteredRuns.map(r => ({ id: r.id, signedDate: r.signedAt, signerName: r.signerName }))
              });
              
              setAgreementRuns(filteredRuns);
            } else {
              console.log('⚠️ No agreements found for template (Review):', template.id);
            }
          }
        } else {
          console.log('⚠️ Template not found for task (Review):', task.templateName);
        }
      }
    } catch (error) {
      console.error('Error loading agreement history:', error);
    } finally {
      setIsLoadingAgreementHistory(false);
    }
  };

  const loadChecklistHistory = async () => {
    setIsLoadingHistory(true);
    try {
      if (!currentOrganization?.id) {
        console.warn('⚠️ No organization ID available for loading checklist history');
        setIsLoadingHistory(false);
        return;
      }
      
      const templateResult = await complianceService.getAllTemplates(currentOrganization.id);
      if (templateResult.success && templateResult.data) {
        // Prefer matching by templateId if available, otherwise match by name
        const template = task.templateId
          ? templateResult.data.find(t => t.id === task.templateId)
          : templateResult.data.find(t => t.name === task.templateName);
        
        if (template?.id) {
          // Filter checklists by template ID only - show only checklists associated with this compliance template
          const checklistsResult = await checklistService.getAllChecklists();
          if (checklistsResult.success && checklistsResult.data) {
            const associatedChecklists = checklistsResult.data.filter(c => {
              // Must have a templateId and it must match the task's template
              if (!c.templateId || c.templateId !== template.id) {
                console.log(`[ReviewTaskModal] Excluding checklist "${c.name}": templateId mismatch`, {
                  checklistTemplateId: c.templateId,
                  taskTemplateId: template.id
                });
                return false;
              }
              
              // If checklist has locationIds specified, must match task's location
              if (c.locationIds && c.locationIds.length > 0) {
                if (!task.locationId || !c.locationIds.includes(task.locationId)) {
                  console.log(`[ReviewTaskModal] Excluding checklist "${c.name}": location mismatch`, {
                    checklistLocationIds: c.locationIds,
                    taskLocationId: task.locationId
                  });
                  return false;
                }
              }
              
              console.log(`[ReviewTaskModal] Including checklist "${c.name}" for template "${template.id}"`);
              return true;
            });
            console.log(`[ReviewTaskModal] Found ${associatedChecklists.length} checklists for template "${template.id}" (${template.name})`, {
              checklists: associatedChecklists.map(c => ({ name: c.name, templateId: c.templateId }))
            });
            const associatedChecklist = associatedChecklists[0]; // Take first match
            
            if (associatedChecklist) {
              setTemplateChecklist(associatedChecklist);
              
               const historyResult = await checklistCompletionService.getCompletionsForChecklist(associatedChecklist.id!);
               if (historyResult.success && historyResult.data) {
                 // Filter checklist runs by the task's location and date range (createdAt to dueDate)
                 // Handle various date formats (Date, Firestore Timestamp, number, string)
                 const getDateFromValue = (value: any): Date => {
                   if (value instanceof Date) {
                     return value;
                   }
                   if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                     return value.toDate();
                   }
                   if (typeof value === 'number') {
                     return new Date(value);
                   }
                   return new Date(value);
                 };
                 
                 // Normalize start date to beginning of day
                 const taskCreatedDate = (() => {
                   const date = getDateFromValue(task.createdAt);
                   date.setHours(0, 0, 0, 0);
                   return date;
                 })();
                 
                 // For completed tasks, use completedAt as end date; otherwise use dueDate
                 // Normalize end date to end of day
                 const taskEndDate = (() => {
                   const endDateValue = task.completedAt || task.dueDate;
                   const date = getDateFromValue(endDateValue);
                   date.setHours(23, 59, 59, 999);
                   return date;
                 })();
                 
                 const filteredRuns = historyResult.data.filter(run => {
                   // Filter by location
                   if (run.locationId !== task.locationId) {
                     return false;
                   }
                   
                   // Filter by date range - only show runs within task period
                   let runDate: Date | null = null;
                   if (run.completedAt?.toDate) {
                     runDate = run.completedAt.toDate();
                   } else if (typeof run.completedAt === 'number') {
                     runDate = new Date(run.completedAt);
                   } else if (run.completedAt instanceof Date) {
                     runDate = run.completedAt;
                   } else if (run.completedAt) {
                     runDate = new Date(run.completedAt);
                   }
                   
                   if (!runDate) {
                     return false; // Skip runs without valid dates
                   }
                   
                   // Run date must be within task period (createdAt to completedAt/dueDate)
                   return runDate >= taskCreatedDate && runDate <= taskEndDate;
                 });
                 
                 setChecklistRuns(filteredRuns);
               }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading checklist history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleOpenTaskConversation = async () => {
    if (!currentOrganization?.id || !user?.id || !task.id) {
      showNotification('Unable to open task conversation. Please ensure you are logged in.', 'error');
      return;
    }

    try {
      console.log('Opening task conversation for task:', task.id, 'messageContext:', task.messageContext);
      const result = await messagingIntegrationService.getOrCreateTaskConversation(
        task.id,
        task.locationId || '',
        task.templateName || 'Task',
        currentOrganization.id,
        user.id,
        task.ownerRole
      );

      if (result.success && result.data?.id) {
        console.log('Opening conversation:', result.data.id);
        // Open messages page in a new window/tab with conversation ID
        window.open(`/messages?conversation=${result.data.id}`, '_blank');
      } else {
        console.error('Failed to get/create conversation:', result.error);
        showNotification(result.error || 'Failed to open task conversation', 'error');
      }
    } catch (error: any) {
      console.error('Error opening task conversation:', error);
      showNotification(error.message || 'Failed to open task conversation', 'error');
    }
  };

  const handleReview = async () => {
    if (!user) {
      showNotification('You must be logged in to review tasks', 'error');
      return;
    }

    setIsReviewing(true);
    try {
      if (completion?.id) {
        let manualTimestamp: number | null = null;
        let aiTimestamp: number | null = null;
        let overrideSource: 'manual' | 'ai' | 'frequency' = 'frequency';

        // Skip due date logic for custom/ad-hoc tasks (one-time tasks)
        if (task.templateId) {
        if (dueDateSelection === 'manual') {
          if (!manualDueDateInput) {
            showNotification('Please select a manual due date before approving.', 'error');
            setIsReviewing(false);
            return;
          }
          const manualDate = new Date(`${manualDueDateInput}T00:00:00`);
          if (isNaN(manualDate.getTime())) {
            showNotification('Manual due date is invalid. Please adjust and try again.', 'error');
            setIsReviewing(false);
            return;
          }
          // Validate that manual date is not after the calculated frequency date
          if (calculatedFrequencyDueDate && manualDate > calculatedFrequencyDueDate) {
            const maxDateStr = calculatedFrequencyDueDate.toLocaleDateString();
            showNotification(`Manual due date cannot be after ${maxDateStr} (based on template frequency). Please select an earlier date.`, 'error');
            setIsReviewing(false);
            return;
          }
          manualTimestamp = manualDate.getTime();
          overrideSource = 'manual';
        } else if (dueDateSelection === 'ai' && suggestedDueDate) {
          aiTimestamp = suggestedDueDate.getTime();
          overrideSource = 'ai';
        } else {
          overrideSource = 'frequency';
          }
        }

        const updatePayload: any = {
          dueDateOverrideSource: overrideSource,
          manualNextDueDate: overrideSource === 'manual' ? manualTimestamp : null,
        };

        if (suggestedDueDate) {
          updatePayload.aiSuggestedDueDate = suggestedDueDate.getTime();
        }

        await taskCompletionService.updateCompletion(completion.id, updatePayload);
        setManualOverrideDate(overrideSource === 'manual' ? new Date(manualTimestamp!) : null);
        setDueDateOverrideSource(overrideSource);
      }

      const result = await reviewTask(task.id, user.id, user.email);
      if (result.success) {
        showNotification('Task reviewed and marked as completed!', 'success');
        await onReview();
        onClose();
      } else {
        showNotification(`Review failed: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error reviewing task:', error);
      showNotification('Failed to review task', 'error');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!user) {
      showNotification('You must be logged in to reject tasks', 'error');
      return;
    }

    if (!rejectionReason.trim()) {
      showNotification('Please provide a reason for rejection', 'error');
      return;
    }

    setIsReviewing(true);
    try {
      const result = await rejectTask(task.id, user.id, user.email, rejectionReason.trim());
      if (result.success) {
        showNotification('Task rejected and sent back to user', 'success');
        await onReview();
        onClose();
      } else {
        showNotification(`Rejection failed: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error rejecting task:', error);
      showNotification('Failed to reject task', 'error');
    } finally {
      setIsReviewing(false);
    }
  };

  const openFile = (url: string) => {
    window.open(url, '_blank');
  };

  const formatDate = (date: any) => {
    try {
      if (date?.toDate) {
        return date.toDate().toLocaleString();
      } else if (date instanceof Date) {
        return date.toLocaleString();
      } else if (typeof date === 'number') {
        return new Date(date).toLocaleString();
      } else {
        return new Date(date).toLocaleString();
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleDueDateSelectionChange = (value: 'manual' | 'ai' | 'frequency') => {
    setDueDateSelection(value);
    // When user selects 'manual', set default date if not already set
    if (value === 'manual' && !manualDueDateInput) {
      // Try to get default date from: manualOverrideDate -> suggestedDueDate -> task.dueDate
      const defaultDate = manualOverrideDate || suggestedDueDate || getDateFromValue(task.dueDate);
      if (defaultDate) {
        setManualDueDateInput(formatDateForInput(defaultDate));
      }
    } else if (value !== 'manual') {
      // Clear manual input when switching away from manual
      setManualDueDateInput('');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[140] p-4">
        {/* Backdrop overlay when run details panel is open - fades the background and main modal */}
        {(selectedRun || selectedAgreementRun) && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-20 z-[141]"
            onClick={() => {
              if (selectedRun) setSelectedRun(null);
              if (selectedAgreementRun) setSelectedAgreementRun(null);
            }}
          />
        )}
        <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto transition-all relative z-[142] ${
          (selectedRun || selectedAgreementRun) ? 'max-w-2xl opacity-60' : 'max-w-3xl'
        }`}>
          <div className="p-3 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Review Task</h3>
                <p className="text-xs text-gray-600 mt-0.5">{task.templateName}</p>
                <p className="text-[11px] text-gray-500 mt-1 font-mono">
                  Task ID: {task.id} • Start: {task.createdAt?.toDate ? task.createdAt.toDate().toLocaleDateString() : (task.createdAt instanceof Date ? task.createdAt.toLocaleDateString() : (typeof task.createdAt === 'number' ? new Date(task.createdAt).toLocaleDateString() : '—'))}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenTaskConversation}
                  className={`p-1 ${
                    task?.id && taskConversationId
                      ? 'text-blue-600 hover:text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title={taskConversationId ? 'Open Task Conversation (conversation exists)' : 'Open Task Conversation'}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* Reference Images */}
            {task.referenceImageUrls && task.referenceImageUrls.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Image className="w-3 h-3 text-blue-600" />
                  Reference Images
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  Compare these reference images with the completion evidence below
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {task.referenceImageUrls.map((imageUrl, index) => {
                    const imageMetadata = task.referenceImageMetadata?.find(meta => meta.url === imageUrl);
                    const note = imageMetadata?.note;
                    const fileName = imageMetadata?.fileName || task.referenceImageFileNames?.[index] || `Image ${index + 1}`;
                    
                    return (
                      <div key={index} className="flex gap-3 p-2 bg-white rounded border border-blue-200 hover:border-blue-300 transition-colors">
                        {/* Image Thumbnail on Left */}
                        <div className="flex-shrink-0 relative group">
                          <img
                            src={imageUrl}
                            alt={fileName}
                            className="w-24 h-24 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setViewingReferenceImageUrls(task.referenceImageUrls || []);
                              setViewingReferenceImageIndex(index);
                              setViewingReferenceImageUrl(imageUrl);
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded flex items-center justify-center transition-opacity cursor-pointer"
                            onClick={() => {
                              setViewingReferenceImageUrls(task.referenceImageUrls || []);
                              setViewingReferenceImageIndex(index);
                              setViewingReferenceImageUrl(imageUrl);
                            }}
                          >
                            <Maximize2 className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                        {/* Note on Right */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {fileName}
                          </div>
                          {note ? (
                            <div className="text-xs text-gray-600 whitespace-pre-wrap">
                              {note}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 italic">
                              No note provided
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reference Documents */}
            {referenceDocuments.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                  <FileText className="w-3 h-3 text-blue-600" />
                  Reference Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {referenceDocuments.map((doc, index) => (
                    <a
                      key={index}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-1.5 bg-white rounded border border-blue-200 hover:border-blue-400 transition-all cursor-pointer text-xs"
                    >
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {doc.type === 'pdf' ? (
                          <FileText className="w-3 h-3 text-red-600 flex-shrink-0" />
                        ) : (
                          <Image className="w-3 h-3 text-green-600 flex-shrink-0" />
                        )}
                        <span className="text-gray-900 truncate" title={doc.name}>
                          {doc.name}
                        </span>
                        <span className="text-gray-500 flex-shrink-0">
                          ({doc.type.toUpperCase()})
                        </span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-blue-600 flex-shrink-0 ml-1" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Task Details */}
            <div className="bg-gray-50 p-2 rounded-lg space-y-1.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-gray-500">Location</span>
                  <p className="text-xs font-medium text-gray-900">{task.locationName}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Category</span>
                  <p className="text-xs font-medium text-gray-900">{task.category}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Owner Role</span>
                  <p className="text-xs font-medium text-gray-900">{task.ownerRole}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Risk Level</span>
                  <p className="text-xs font-medium text-gray-900">{task.riskLevel}</p>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Pass/Fail Criteria</span>
                <p className="text-xs text-gray-900 mt-0.5">{task.passFailCriteria}</p>
              </div>
            </div>

            {/* Completion Details */}
            {isLoadingCompletion ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-xs text-gray-500 mt-2">Loading completion details...</p>
              </div>
            ) : completion ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1.5">Completion Details</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-xs text-gray-500">Completed By</span>
                      <p className="text-xs font-medium text-gray-900">{completion.completedByName || completion.completedBy || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Completion Date</span>
                      <p className="text-xs font-medium text-gray-900">{formatDate(completion.completionDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Comments */}
                {completion.comments && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comments</label>
                    <div className="bg-gray-50 p-2 rounded text-xs text-gray-900 border border-gray-200">
                      {typeof completion.comments === 'string' 
                        ? completion.comments 
                        : typeof completion.comments === 'object' 
                          ? JSON.stringify(completion.comments) 
                          : String(completion.comments)}
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {completion.notes && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Additional Notes</label>
                    <div className="bg-gray-50 p-2 rounded text-xs text-gray-900 border border-gray-200">
                      {typeof completion.notes === 'string' 
                        ? completion.notes 
                        : typeof completion.notes === 'object' 
                          ? JSON.stringify(completion.notes) 
                          : String(completion.notes)}
                    </div>
                  </div>
                )}

                {/* Electronic Signature */}
                {completion.signature && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Electronic Signature</label>
                    <div className="bg-gray-50 p-2 rounded border border-gray-200">
                      <div className="space-y-2">
                        {(completion.signature.signatureUrl || completion.signature.signatureDataUrl) && (
                          <div className="bg-white p-2 rounded border border-gray-300">
                            <img
                              src={completion.signature.signatureUrl || completion.signature.signatureDataUrl}
                              alt="Signature"
                              className="max-w-full h-auto border border-gray-200 rounded"
                              style={{ maxHeight: '150px' }}
                            />
                          </div>
                        )}
                        <div className="text-xs space-y-0.5">
                          {completion.signature.signerName && (
                            <div>
                              <span className="font-medium text-gray-700">Signed by:</span>{' '}
                              <span className="text-gray-900">{completion.signature.signerName}</span>
                            </div>
                          )}
                          {completion.signature.signerEmail && (
                            <div>
                              <span className="font-medium text-gray-700">Email:</span>{' '}
                              <span className="text-gray-900">{completion.signature.signerEmail}</span>
                            </div>
                          )}
                          {completion.signature.signedAt && (
                            <div>
                              <span className="font-medium text-gray-700">Signed at:</span>{' '}
                              <span className="text-gray-900">
                                {completion.signature.signedAt instanceof Date
                                  ? completion.signature.signedAt.toLocaleString()
                                  : typeof completion.signature.signedAt === 'number'
                                  ? new Date(completion.signature.signedAt).toLocaleString()
                                  : new Date(completion.signature.signedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Evidence Files */}
                {completion.evidenceUrls && completion.evidenceUrls.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Uploaded Evidence Files</label>
                    <div className="space-y-1">
                      {completion.evidenceUrls.map((url, idx) => {
                        const fileName = completion.evidenceFileNames?.[idx] || `Evidence File ${idx + 1}`;
                        const analysis = completion.documentAnalyses?.[fileName];
                        const fileNote = completion.fileNotes?.[fileName];
                        
                        // Debug logging
                        if (idx === 0) {
                          console.log('🔍 Review Modal - File:', fileName);
                          console.log('🔍 Review Modal - All file names:', completion.evidenceFileNames);
                          console.log('🔍 Review Modal - All analyses keys:', completion.documentAnalyses ? Object.keys(completion.documentAnalyses) : 'none');
                          console.log('🔍 Review Modal - Analysis for this file:', analysis);
                        }
                        
                        if (url.startsWith('UPLOAD_FAILED:')) {
                          return (
                            <div key={idx} className="flex items-center justify-between p-1.5 bg-red-50 rounded text-xs border border-red-200">
                              <div className="flex items-center gap-1.5">
                                <AlertCircle className="w-3 h-3 text-red-600" />
                                <span className="text-red-900">Upload Failed: {url.replace('UPLOAD_FAILED:', '')}</span>
                              </div>
                            </div>
                          );
                        }
                        const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
                        
                        return (
                          <div key={idx} className="p-2 bg-gray-50 rounded text-xs border border-gray-200">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <Paperclip className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                <span className="text-gray-900 truncate">{fileName}</span>
                                {analysis && (
                                  <Sparkles className="w-3 h-3 text-purple-600 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  onClick={() => openFile(url)}
                                  className="btn-primary text-xs px-2 py-0.5 flex items-center gap-1"
                                  title="View File"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View
                                </button>
                                <a
                                  href={url}
                                  download
                                  className="btn-secondary text-xs px-2 py-0.5 flex items-center gap-1"
                                  title="Download File"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              </div>
                            </div>
                            
                            {/* Image Preview and Analysis Layout */}
                            <div className={`flex gap-2 ${isImage ? 'flex-row' : 'flex-col'}`}>
                              {/* Image Preview (Left) */}
                              {isImage && (
                                <div className="flex-shrink-0 w-32 h-32 border border-gray-300 rounded bg-white flex items-center justify-center overflow-hidden">
                                  <img 
                                    src={url} 
                                    alt={fileName}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      // Hide image if it fails to load
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Analysis and Notes (Right) */}
                              <div className={`flex-1 min-w-0 ${isImage ? '' : 'mt-1.5'}`}>
                                {/* Document Analysis Results */}
                                {analysis ? (
                                  <div className="mt-1.5 p-1.5 bg-white rounded border border-purple-200">
                                <div className="text-xs space-y-1">
                                  <div>
                                    <span className="font-medium text-purple-700">Type:</span>{' '}
                                    <span className="text-gray-900">{analysis.documentType || 'Unknown'}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-purple-700">Summary:</span>{' '}
                                    <span className="text-gray-900">{analysis.summary || 'No summary available'}</span>
                                  </div>
                                  {analysis.expiryDate && (
                                    <div className="bg-orange-50 border border-orange-200 rounded p-1 mt-1">
                                      <span className="font-medium text-orange-700">Expiry Date Found:</span>{' '}
                                      <span className="text-gray-900">
                                        {typeof analysis.expiryDate === 'string' 
                                          ? new Date(analysis.expiryDate).toLocaleDateString()
                                          : analysis.expiryDate?.toLocaleDateString() || 'Invalid date'}
                                      </span>
                                    </div>
                                  )}
                                  {analysis.extractedDates && analysis.extractedDates.length > 0 && (
                                    <div className="mt-1">
                                      <span className="font-medium text-purple-700">All Dates:</span>
                                      <div className="space-y-0.5 mt-0.5">
                                        {analysis.extractedDates.map((dateInfo: any, dIdx: number) => {
                                          const date = typeof dateInfo.date === 'string' 
                                            ? new Date(dateInfo.date) 
                                            : (dateInfo.date instanceof Date ? dateInfo.date : new Date(dateInfo.date));
                                          return (
                                            <div key={dIdx} className="text-gray-700 text-xs">
                                              {dateInfo.type}: {date.toLocaleDateString()} ({dateInfo.confidence} confidence)
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {analysis.keyInformation && Object.keys(analysis.keyInformation).length > 0 && (
                                    <div className="mt-1">
                                      <span className="font-medium text-purple-700">Key Info:</span>
                                      <div className="space-y-0.5 mt-0.5">
                                        {Object.entries(analysis.keyInformation).map(([key, value]) => {
                                          // Safely convert value to string - handle objects, arrays, null, undefined
                                          let displayValue: string;
                                          if (value === null || value === undefined) {
                                            displayValue = 'N/A';
                                          } else if (typeof value === 'object') {
                                            // If it's an object or array, stringify it
                                            try {
                                              displayValue = JSON.stringify(value);
                                            } catch {
                                              displayValue = String(value);
                                            }
                                          } else {
                                            displayValue = String(value);
                                          }
                                          return (
                                            <div key={key} className="text-gray-700 text-xs">
                                              {key}: {displayValue}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  </div>
                                </div>
                              ) : null}
                              
                              {/* File Notes */}
                              {fileNote && (
                                <div className="mt-1.5 p-1.5 bg-blue-50 rounded border border-blue-200">
                                  <div className="text-xs">
                                    <span className="font-medium text-blue-700">Notes:</span>
                                    <div className="text-gray-900 mt-0.5 whitespace-pre-wrap">{fileNote}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

            {/* Next Due Date Review - Hide for custom/ad-hoc tasks (one-time tasks) */}
            {shouldShowDueDateReview && task.templateId && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs shadow-sm space-y-2">
                <div className="flex items-center gap-2 text-blue-800 font-medium uppercase tracking-wide">
                  <Calendar className="w-3 h-3" />
                  Next Due Date Review
                </div>
                {suggestedDueDate ? (
                  <div className="text-gray-600">
                    AI detected an expiry date and suggested <strong>{suggestedDueDate.toLocaleDateString()}</strong>{' '}
                    ({task.leadTimeDays || 30} days before expiry). You can keep that, set a manual date, or fall back to template frequency before approving.
                  </div>
                ) : manualOverrideDate ? (
                  <div className="text-gray-600">
                    No AI expiry date detected. The submitter set a manual date. You can keep it, change it, or revert to the template frequency.
                  </div>
                ) : (
                  <div className="text-gray-600">
                    No expiry date was detected in the uploaded documents. You can set the next due date manually or let Complin calculate it automatically using the template frequency.
                  </div>
                )}
                <div className="space-y-1.5 text-gray-700">
                  {suggestedDueDate && (
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="review-due-date-selection"
                        value="ai"
                        checked={dueDateSelection === 'ai'}
                        onChange={() => handleDueDateSelectionChange('ai')}
                      />
                      <span>Use AI suggested due date ({suggestedDueDate.toLocaleDateString()})</span>
                    </label>
                  )}
                  <label className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="review-due-date-selection"
                        value="manual"
                        checked={dueDateSelection === 'manual'}
                        onChange={() => handleDueDateSelectionChange('manual')}
                      />
                      <span>Set next due date manually</span>
                    </span>
                    {dueDateSelection === 'manual' && (
                      <div className="flex flex-col gap-1">
                        <input
                          type="date"
                          value={manualDueDateInput}
                          onChange={(event) => {
                            const selectedDate = event.target.value;
                            if (maxDateInput && selectedDate > maxDateInput) {
                              // Prevent selecting date after calculated frequency date
                              return;
                            }
                            setManualDueDateInput(selectedDate);
                          }}
                          max={maxDateInput}
                          className="border border-gray-300 rounded px-2 py-1 mt-1 w-fit"
                          required
                        />
                        {maxDateInput && (
                          <div className="text-gray-500 text-[11px]">
                            Maximum date: {new Date(maxDateInput + 'T00:00:00').toLocaleDateString()} (based on template frequency)
                          </div>
                        )}
                        {dueDateSelection === 'manual' && manualDueDateInput && (
                          <div className="text-gray-500 text-[11px]">Reviewer override: {manualDueDateInput}</div>
                        )}
                      </div>
                    )}
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="review-due-date-selection"
                      value="frequency"
                      checked={dueDateSelection === 'frequency'}
                      onChange={() => handleDueDateSelectionChange('frequency')}
                    />
                    <span>
                      Let Complin calculate automatically using template frequency
                      {calculatedFrequencyDueDate && (
                        <span className="ml-2 font-medium text-blue-700">
                          (Next due date: {calculatedFrequencyDueDate.toLocaleDateString()})
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            )}
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-xs">No completion data found for this task.</p>
              </div>
            )}

            {/* Related Assets with Completed Work */}
            {relatedAssets.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-primary-600" />
                  Related Assets ({relatedAssets.length} {relatedAssets.length === 1 ? 'asset' : 'assets'})
                </h4>
                
                {isLoadingAssets ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-2">Loading assets and completions...</p>
                  </div>
                ) : (() => {
                  // Prepare assets with completions data
                  const assetsWithCompletions = relatedAssets.map(asset => {
                    const assetComp = assetCompletions.find(ac => ac.assetId === asset.id) || {
                      assetId: asset.id!,
                      assetName: asset.assetName,
                      checklistCompletions: [],
                      formCompletions: []
                    };
                    
                    // Combine all checklists and forms
                    const allCompletions = [
                      ...assetComp.checklistCompletions.map(cc => ({
                        type: 'checklist' as const,
                        id: cc.id || '',
                        name: cc.checklistName,
                        completionDate: cc.completedAt,
                        completedBy: cc.completedBy,
                        data: cc
                      })),
                      ...assetComp.formCompletions.map(fc => ({
                        type: 'form' as const,
                        id: fc.id || '',
                        name: fc.formName,
                        completionDate: fc.completionDate,
                        completedBy: fc.completedBy,
                        data: fc
                      }))
                    ];
                    
                    return {
                      asset,
                      assetComp,
                      allCompletions
                    };
                  });

                  // Sort based on selected option
                  let sortedAssets = [...assetsWithCompletions];
                  const direction = assetSortDirection === 'asc' ? 1 : -1;
                  
                  if (assetSortBy === 'name') {
                    sortedAssets.sort((a, b) => {
                      const comparison = a.asset.assetName.localeCompare(b.asset.assetName);
                      return direction * comparison;
                    });
                  } else if (assetSortBy === 'completions') {
                    sortedAssets.sort((a, b) => {
                      return direction * (b.allCompletions.length - a.allCompletions.length);
                    });
                  } else if (assetSortBy === 'pending') {
                    // For pending, we'll sort by assets with fewer completions
                    sortedAssets.sort((a, b) => {
                      return direction * (a.allCompletions.length - b.allCompletions.length);
                    });
                  }

                  return (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border-collapse border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (assetSortBy === 'name') {
                                    setAssetSortDirection(assetSortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setAssetSortBy('name');
                                    setAssetSortDirection('asc');
                                  }
                                }}
                                className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                              >
                                Asset Name
                                <ArrowUpDown className={`w-4 h-4 ${assetSortBy === 'name' ? 'text-primary-600' : 'text-gray-400'}`} />
                              </button>
                            </th>
                            <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (assetSortBy === 'completions') {
                                    setAssetSortDirection(assetSortDirection === 'asc' ? 'desc' : 'asc');
                                  } else {
                                    setAssetSortBy('completions');
                                    setAssetSortDirection('desc');
                                  }
                                }}
                                className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                              >
                                Checklists & Forms
                                <ArrowUpDown className={`w-4 h-4 ${assetSortBy === 'completions' ? 'text-primary-600' : 'text-gray-400'}`} />
                              </button>
                            </th>
                          {allEvidenceTypes.length > 0 && (
                            <th className="px-4 py-2 text-left border border-gray-200 font-semibold text-gray-700">
                              Required Evidence
                            </th>
                          )}
                          </tr>
                        </thead>
                        <tbody>
                        {sortedAssets.map(({ asset, allCompletions }) => {
                          const evidenceEntry = completion?.assetEvidence?.find(entry => entry.assetId === asset.id);
                          const evidenceByKey = new Map<string, any>();
                          if (evidenceEntry?.evidence) {
                            evidenceEntry.evidence.forEach(item => {
                              evidenceByKey.set(item.key, item);
                            });
                          }
                          const assetEvidenceStatuses = allEvidenceTypes.map((evidence, idx) => {
                            const key = getEvidenceKey(evidence, idx);
                            const item = evidenceByKey.get(key);
                            const satisfied = !!item && item.url && !item.url.startsWith('UPLOAD_FAILED');
                            return { evidence, key, item, satisfied };
                          });
                          const assetHasMissingRequired = requiredEvidenceList.length > 0 && assetEvidenceStatuses.some(status => status.evidence.required && !status.satisfied);

                          return (
                            <tr key={asset.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900">
                                {asset.assetName}
                                {asset.assetLocation && (
                                  <span className="text-xs text-gray-500 ml-2">({asset.assetLocation})</span>
                                )}
                              </td>
                              <td className="px-4 py-3 border border-gray-200">
                                {allCompletions.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {allCompletions.map((completion, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (completion.type === 'checklist') {
                                            handleViewCompletion(completion.data as ChecklistCompletion, 'checklist');
                                          } else {
                                            handleViewCompletion(completion.data as FormCompletion, 'form');
                                          }
                                        }}
                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors cursor-pointer flex items-center gap-1"
                                      >
                                        {completion.type === 'checklist' ? (
                                          <ClipboardList className="w-3 h-3" />
                                        ) : (
                                          <FileText className="w-3 h-3" />
                                        )}
                                        {completion.name}
                                        {completion.completionDate && (
                                          <span className="text-blue-600 ml-1">
                                            ({completion.completionDate?.toDate ? 
                                              completion.completionDate.toDate().toLocaleDateString() :
                                              (typeof completion.completionDate === 'number' ? 
                                                new Date(completion.completionDate).toLocaleDateString() :
                                                (completion.completionDate instanceof Date ? 
                                                  completion.completionDate.toLocaleDateString() : ''))})
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No completions</span>
                                )}
                              </td>
                            {allEvidenceTypes.length > 0 && (
                              <td className="px-4 py-3 border border-gray-200 align-top">
                                {assetEvidenceStatuses.length > 0 ? (
                                  <div className="space-y-2">
                                    {allEvidenceTypes.map((evidence, idx) => {
                                      const { key, item, satisfied } = assetEvidenceStatuses[idx];
                                      const isRequired = evidence.required;
                                      return (
                                        <div key={key} className="border border-gray-200 rounded-md p-2 bg-gray-50">
                                          <div className="flex items-center justify-between gap-2 text-xs font-medium text-gray-800">
                                            <span>
                                              {evidence.label}{' '}
                                              <span className="text-gray-500 font-normal">({evidence.type})</span>
                                              {isRequired && <span className="text-red-600 ml-1">*</span>}
                                            </span>
                                            {satisfied ? (
                                              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                            ) : (
                                              isRequired && <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                                            )}
                                          </div>
                                          {item ? (
                                            item.url.startsWith('UPLOAD_FAILED:') ? (
                                              <div className="mt-1 text-[11px] text-red-600">
                                                Upload failed for {item.fileName || 'file'}
                                              </div>
                                            ) : (
                                              <div className="mt-1">
                                                <a
                                                  href={item.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-xs text-blue-600 hover:underline break-all"
                                                >
                                                  {item.fileName || 'View evidence'}
                                                </a>
                                              </div>
                                            )
                                          ) : (
                                            <div className="mt-1 text-[11px] text-gray-500 italic">
                                              {isRequired ? 'Missing evidence' : 'Not provided'}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {assetHasMissingRequired && (
                                      <div className="flex items-center gap-1 text-xs text-orange-600">
                                        <AlertCircle className="w-3 h-3" />
                                        <span>Required evidence missing for this asset.</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No evidence provided</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                        })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Agreement Run History */}
            {templateAgreements.length > 0 && (
              <div className="border-t pt-2 mt-2">
                <h4 className="text-xs font-medium text-gray-700 mb-1.5">
                  Agreements - Past Signatures ({agreementRuns.length > 0 ? agreementRuns.length : 0} {agreementRuns.length === 1 ? 'signature' : 'signatures'})
                </h4>
                
                {isLoadingAgreementHistory ? (
                  <div className="text-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-xs text-gray-500 mt-1">Loading...</p>
                  </div>
                ) : agreementRuns.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border-collapse">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-1.5 py-1 text-left border border-gray-200">Contract Name</th>
                            <th className="px-1.5 py-1 text-left border border-gray-200">Signer</th>
                            <th className="px-1.5 py-1 text-left border border-gray-200">Key Dates</th>
                            <th className="px-1.5 py-1 text-left border border-gray-200">AI Summary</th>
                            <th className="px-1.5 py-1 text-left border border-gray-200">View</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agreementRuns.map((run) => {
                            const agreement = templateAgreements.find(a => a.id === run.agreementId);
                            
                            // Robust date parsing function (same logic as used in filtering)
                            const parseSignedDate = (completion: AgreementCompletion): Date | null => {
                              let runDate: Date | null = null;
                              
                              // Try to get date from signedAt first
                              if (completion.signedAt) {
                                try {
                                  // Handle Firestore Timestamp explicitly
                                  if (completion.signedAt && typeof completion.signedAt === 'object') {
                                    // Check if it's a Firestore Timestamp with toDate
                                    if ('toDate' in completion.signedAt && typeof completion.signedAt.toDate === 'function') {
                                      runDate = completion.signedAt.toDate();
                                    }
                                    // Check if it has _seconds property (Timestamp format)
                                    else if ('_seconds' in completion.signedAt || 'seconds' in completion.signedAt) {
                                      const seconds = completion.signedAt._seconds || completion.signedAt.seconds || 0;
                                      const nanoseconds = completion.signedAt._nanoseconds || completion.signedAt.nanoseconds || 0;
                                      runDate = new Date(seconds * 1000 + nanoseconds / 1000000);
                                    }
                                    // Check if it's a serverTimestamp placeholder
                                    else if (completion.signedAt._methodName === 'serverTimestamp') {
                                      // Use clientSignedAt as fallback
                                      if (completion.clientSignedAt) {
                                        runDate = new Date(completion.clientSignedAt);
                                      }
                                    } else {
                                      // Try generic date conversion
                                      if (completion.signedAt instanceof Date) {
                                        runDate = completion.signedAt;
                                      } else if (typeof completion.signedAt === 'number') {
                                        runDate = completion.signedAt < 1000000000000 ? new Date(completion.signedAt * 1000) : new Date(completion.signedAt);
                                      } else if (typeof completion.signedAt === 'string') {
                                        runDate = new Date(completion.signedAt);
                                      }
                                    }
                                  } else if (completion.signedAt instanceof Date) {
                                    runDate = completion.signedAt;
                                  } else if (typeof completion.signedAt === 'number') {
                                    runDate = completion.signedAt < 1000000000000 ? new Date(completion.signedAt * 1000) : new Date(completion.signedAt);
                                  } else if (typeof completion.signedAt === 'string') {
                                    runDate = new Date(completion.signedAt);
                                  }
                                } catch (e) {
                                  console.log('⚠️ Error parsing signedAt for run (Review):', run.id, e);
                                }
                              }
                              
                              // Fallback to clientSignedAt if signedAt didn't work
                              if ((!runDate || isNaN(runDate.getTime())) && completion.clientSignedAt) {
                                runDate = new Date(completion.clientSignedAt);
                              }
                              
                              return (runDate && !isNaN(runDate.getTime())) ? runDate : null;
                            };
                            
                            const signedDate = parseSignedDate(run);

                            // Parse expiry date for display
                            const parseExpiryDate = (expiryDate: any): Date | null => {
                              if (!expiryDate) return null;
                              if (expiryDate instanceof Date) return expiryDate;
                              if (typeof expiryDate === 'string') {
                                const parsed = new Date(expiryDate);
                                return isNaN(parsed.getTime()) ? null : parsed;
                              }
                              if (expiryDate?.toDate && typeof expiryDate.toDate === 'function') {
                                return expiryDate.toDate();
                              }
                              return null;
                            };

                            const expiryDate = parseExpiryDate(run.expiryDate);
                            const now = new Date();
                            const daysUntilExpiry = expiryDate 
                              ? Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                              : null;
                            
                            // Parse contract date
                            const parseContractDate = (dateValue: any): Date | null => {
                              if (!dateValue) return null;
                              if (dateValue instanceof Date) return dateValue;
                              if (typeof dateValue === 'string') {
                                const parsed = new Date(dateValue);
                                return isNaN(parsed.getTime()) ? null : parsed;
                              }
                              if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
                                return dateValue.toDate();
                              }
                              return null;
                            };

                            const contractDate = parseContractDate(run.contractDate);

                            return (
                              <tr 
                                key={run.id} 
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => setSelectedAgreementRun(run)}
                              >
                                <td className="px-1.5 py-1 border border-gray-200 text-gray-700 font-medium">
                                  {agreement?.name || run.agreementName || 'Unknown'}
                                </td>
                                <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                  {run.signerName || 'Unknown'}
                                </td>
                                <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                  <div className="space-y-0.5 text-xs">
                                    {contractDate ? (
                                      <div>Signed: {contractDate.toLocaleDateString()}</div>
                                    ) : signedDate ? (
                                      <div>Signed: {signedDate.toLocaleDateString()}</div>
                                    ) : null}
                                    {expiryDate && (
                                      <div className={`font-semibold ${
                                        daysUntilExpiry !== null && daysUntilExpiry < 0 
                                          ? 'text-red-600' 
                                          : daysUntilExpiry !== null && daysUntilExpiry < 30 
                                          ? 'text-orange-600' 
                                          : 'text-green-600'
                                      }`}>
                                        Expires: {expiryDate.toLocaleDateString()}
                                      </div>
                                    )}
                                    {!contractDate && !signedDate && !expiryDate && (
                                      <div className="text-gray-400 italic">No dates</div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-1.5 py-1 border border-gray-200 text-gray-700">
                                  {run.contractSummary ? (
                                    <div className="text-xs max-w-xs" title={run.contractSummary}>
                                      {run.contractSummary.substring(0, 80)}...
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400 italic">No AI analysis</div>
                                  )}
                                </td>
                                <td className="px-1.5 py-1 border border-gray-200">
                                  {run.signedPdfUrl && (
                                    <a
                                      href={run.signedPdfUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-blue-600 hover:text-blue-800 text-xs"
                                    >
                                      <Eye className="w-3 h-3" />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">No past signatures found.</p>
                )}
              </div>
            )}

            {/* Rejection Reason Input */}
            {showRejectInput && (
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this task (e.g., incorrect file uploaded, missing information, etc.)"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  rows={3}
                  disabled={isReviewing}
                />
                <p className="text-xs text-gray-500 mt-1">This reason will be visible to the user when the task is sent back.</p>
              </div>
            )}

            {/* AI Analysis Status Banner */}
            {(isAnalyzingEvidence || pendingAnalyses.size > 0) && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600 animate-pulse" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-blue-900">
                    AI Analysis in Progress
                  </div>
                  <div className="text-xs text-blue-700 mt-0.5">
                    Analyzing {pendingAnalyses.size} file{pendingAnalyses.size !== 1 ? 's' : ''}...
                    {analyzingFile && ` (${analyzingFile})`}
                  </div>
                </div>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={onClose}
                className="btn-secondary px-4 py-2 text-xs"
                disabled={isReviewing || isAnalyzingEvidence || pendingAnalyses.size > 0}
              >
                Cancel
              </button>
              {!showRejectInput ? (
                <>
                  <button
                    onClick={() => setShowRejectInput(true)}
                    disabled={isReviewing}
                    className="px-4 py-2 text-xs flex items-center gap-1.5 border border-red-500 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <XCircle className="w-3 h-3" />
                    Reject
                  </button>
                  <button
                    onClick={handleReview}
                    disabled={isReviewing || isAnalyzingEvidence || pendingAnalyses.size > 0}
                    className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      isAnalyzingEvidence || pendingAnalyses.size > 0
                        ? `AI analysis in progress for ${pendingAnalyses.size} file${pendingAnalyses.size !== 1 ? 's' : ''}...`
                        : ''
                    }
                  >
                    {isReviewing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Reviewing...
                      </>
                    ) : isAnalyzingEvidence || pendingAnalyses.size > 0 ? (
                      <>
                        <Sparkles className="w-3 h-3 animate-pulse" />
                        Analyzing... ({pendingAnalyses.size})
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Mark as Reviewed
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowRejectInput(false);
                      setRejectionReason('');
                    }}
                    disabled={isReviewing}
                    className="btn-secondary px-4 py-2 text-xs"
                  >
                    Cancel Rejection
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={isReviewing || !rejectionReason.trim()}
                    className="px-4 py-2 text-xs flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    {isReviewing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3" />
                        Confirm Rejection
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Checklist Run Details Panel */}
        {selectedRun && templateChecklist && (
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[143] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Checklist Run Details</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {formatDate(selectedRun.completedAt)}
                </p>
              </div>
              <button
                onClick={() => setSelectedRun(null)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {/* Variables */}
              {selectedRun.variableValues && Object.keys(selectedRun.variableValues).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                  <div className="space-y-1">
                    {Object.entries(selectedRun.variableValues).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-700">{key}:</span>{' '}
                        <span className="text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completion Info */}
              <div className="bg-gray-50 border border-gray-200 rounded p-2">
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium text-gray-700">Location:</span>{' '}
                    <span className="text-gray-900">{selectedRun.locationName || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Completed By:</span>{' '}
                    <span className="text-gray-900">{selectedRun.completedByName || selectedRun.completedBy || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Checklist Items */}
              <div>
                <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                  Checklist Items ({Object.values(selectedRun.items || {}).filter(v => v).length}/{templateChecklist.items?.length || 0} completed)
                </h4>
                <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {templateChecklist.items?.map((item, index) => {
                    // Use items (mobile format)
                    const items = selectedRun.items || {};
                    const isCompleted = items[item.text] || false;
                    const photoData = selectedRun.photos?.[item.text];
                    // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                    const photos = photoData 
                      ? (Array.isArray(photoData) ? photoData : [photoData])
                      : [];
                    const taskRefs = (selectedRun.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                    
                    return (
                      <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                        <div className="flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-900">
                            <span className="font-medium">{index + 1}.</span> {item.text}
                          </div>
                          {isCompleted && taskRefs.length > 0 && (
                            <div className="mt-1 text-xs text-blue-600 font-medium">
                              ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                            </div>
                          )}
                          {photos.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-2">
                              {photos.map((photoUrl, photoIndex) => (
                                <img 
                                  key={photoIndex}
                                  src={photoUrl} 
                                  alt={`${item.text} photo ${photoIndex + 1}`}
                                  className="max-w-full h-auto rounded border border-gray-300 max-h-32"
                                  onClick={() => window.open(photoUrl, '_blank')}
                                  style={{ cursor: 'pointer' }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Run Details Panel */}
        {selectedFormRun && templateForm && (
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[143] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Form Run Details</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {selectedFormRun.completionDate?.toDate 
                    ? selectedFormRun.completionDate.toDate().toLocaleString()
                    : typeof selectedFormRun.completionDate === 'number'
                    ? new Date(selectedFormRun.completionDate).toLocaleString()
                    : 'Unknown date'}
                </p>
              </div>
              <button
                onClick={() => setSelectedFormRun(null)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {/* Variables */}
              {selectedFormRun.variableValues && Object.keys(selectedFormRun.variableValues).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                  <div className="space-y-1">
                    {Object.entries(selectedFormRun.variableValues).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-700">{key}:</span>{' '}
                        <span className="text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {selectedFormRun.aiSummary && (
                <div className="bg-purple-50 border border-purple-200 rounded p-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-purple-600" />
                    AI Summary
                  </h4>
                  <p className="text-xs text-gray-700 italic">{selectedFormRun.aiSummary}</p>
                </div>
              )}

              {/* Completion Info */}
              <div className="bg-gray-50 border border-gray-200 rounded p-2">
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium text-gray-700">Location:</span>{' '}
                    <span className="text-gray-900">{selectedFormRun.locationName || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Completed By:</span>{' '}
                    <span className="text-gray-900">{selectedFormRun.completedBy || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                  Form Fields
                </h4>
                <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                  {templateForm.fields?.map((field, index) => {
                    const fieldValue = selectedFormRun.fieldValues?.[field.label];
                    return (
                      <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs">
                            <span className="font-medium text-gray-700">{field.label}:</span>{' '}
                            <span className="text-gray-900">
                              {fieldValue !== null && fieldValue !== undefined && fieldValue !== '' 
                                ? String(fieldValue) 
                                : <span className="text-gray-400 italic">Not provided</span>}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Agreement Run Details Panel */}
      {selectedAgreementRun && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[144] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Agreement Signature Details</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {(() => {
                  // Prefer AI-extracted contract date over upload date
                  const parseContractDate = (dateValue: any): Date | null => {
                    if (!dateValue) return null;
                    if (dateValue instanceof Date) {
                      return isNaN(dateValue.getTime()) ? null : dateValue;
                    }
                    if (typeof dateValue === 'string') {
                      const parsed = new Date(dateValue);
                      return isNaN(parsed.getTime()) ? null : parsed;
                    }
                    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
                      try {
                        const date = dateValue.toDate();
                        return isNaN(date.getTime()) ? null : date;
                      } catch (e) {
                        return null;
                      }
                    }
                    // Handle Firestore timestamp with _seconds
                    if (dateValue && typeof dateValue === 'object' && ('_seconds' in dateValue || 'seconds' in dateValue)) {
                      const seconds = dateValue._seconds || dateValue.seconds || 0;
                      const nanoseconds = dateValue._nanoseconds || dateValue.nanoseconds || 0;
                      return new Date(seconds * 1000 + nanoseconds / 1000000);
                    }
                    return null;
                  };
                  
                  const contractDate = parseContractDate(selectedAgreementRun.contractDate);
                  if (contractDate) {
                    return contractDate.toLocaleString();
                  }
                  
                  // Fallback to upload date if no contract date
                  const getDateFromValue = (value: any): Date | null => {
                    if (!value) return null;
                    if (value instanceof Date) {
                      return isNaN(value.getTime()) ? null : value;
                    }
                    // Handle serverTimestamp placeholder
                    if (value && typeof value === 'object' && value._methodName === 'serverTimestamp') {
                      // Use clientSignedAt as fallback
                      if (selectedAgreementRun.clientSignedAt) {
                        return new Date(selectedAgreementRun.clientSignedAt);
                      }
                      return null;
                    }
                    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
                      try {
                        const date = value.toDate();
                        return isNaN(date.getTime()) ? null : date;
                      } catch (e) {
                        return null;
                      }
                    }
                    if (typeof value === 'number') {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    if (value && typeof value === 'object' && ('_seconds' in value || 'seconds' in value)) {
                      const seconds = value._seconds || value.seconds || 0;
                      const nanoseconds = value._nanoseconds || value.nanoseconds || 0;
                      return new Date(seconds * 1000 + nanoseconds / 1000000);
                    }
                    if (typeof value === 'string') {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    return null;
                  };
                  const signedDate = selectedAgreementRun.signedAt ? getDateFromValue(selectedAgreementRun.signedAt) : 
                                    (selectedAgreementRun.clientSignedAt ? new Date(selectedAgreementRun.clientSignedAt) : null);
                  return signedDate ? signedDate.toLocaleString() : 'Unknown date';
                })()}
              </p>
            </div>
            <button
              onClick={() => setSelectedAgreementRun(null)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {/* Agreement Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="space-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-700">Agreement:</span>{' '}
                  <span className="text-gray-900">{selectedAgreementRun.agreementName || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Signer Name:</span>{' '}
                  <span className="text-gray-900">{selectedAgreementRun.signerName || 'Unknown'}</span>
                </div>
                {selectedAgreementRun.signerEmail && (
                  <div>
                    <span className="font-medium text-gray-700">Signer Email:</span>{' '}
                    <span className="text-gray-900">{selectedAgreementRun.signerEmail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary - Prominent Display */}
            <div className="bg-purple-50 border border-purple-200 rounded p-3">
              <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-purple-600" />
                AI Analysis Summary
              </h4>
              {selectedAgreementRun.contractSummary ? (
                <p className="text-xs text-gray-900 leading-relaxed">{selectedAgreementRun.contractSummary}</p>
              ) : (
                <p className="text-xs text-gray-500 italic">No AI analysis available for this contract.</p>
              )}
            </div>

            {/* AI-Parsed Contract Information */}
            {(selectedAgreementRun.contractSummary || selectedAgreementRun.contractDate || selectedAgreementRun.expiryDate || selectedAgreementRun.contractKeyInformation) && (
              <div className="bg-purple-50 border border-purple-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-purple-600" />
                  Contract Summary (AI-Parsed)
                </h4>
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    {selectedAgreementRun.expiryDate && (
                      <div>
                        <span className="font-medium text-gray-700">Expiry Date:</span>
                        <p className={`font-semibold ${
                          (() => {
                            const expiry = typeof selectedAgreementRun.expiryDate === 'string' 
                              ? new Date(selectedAgreementRun.expiryDate)
                              : selectedAgreementRun.expiryDate?.toDate?.() || selectedAgreementRun.expiryDate;
                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'text-gray-900';
                            const now = new Date();
                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            if (daysUntilExpiry < 0) return 'text-red-600';
                            if (daysUntilExpiry < 30) return 'text-orange-600';
                            return 'text-green-600';
                          })()
                        }`}>
                          {(() => {
                            const expiry = typeof selectedAgreementRun.expiryDate === 'string' 
                              ? new Date(selectedAgreementRun.expiryDate)
                              : selectedAgreementRun.expiryDate?.toDate?.() || selectedAgreementRun.expiryDate;
                            if (!(expiry instanceof Date) || isNaN(expiry.getTime())) return 'Not available';
                            const formatted = expiry.toLocaleDateString();
                            const now = new Date();
                            const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            if (daysUntilExpiry < 0) return `${formatted} (Expired ${Math.abs(daysUntilExpiry)} days ago)`;
                            if (daysUntilExpiry < 30) return `${formatted} (Expires in ${daysUntilExpiry} days)`;
                            return formatted;
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                  {selectedAgreementRun.contractKeyInformation && Object.keys(selectedAgreementRun.contractKeyInformation).length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Key Information:</span>
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(selectedAgreementRun.contractKeyInformation).map(([key, value]) => {
                          // Convert value to string if it's an object
                          let displayValue: string;
                          if (value === null || value === undefined) {
                            displayValue = 'N/A';
                          } else if (typeof value === 'object') {
                            // If it's an object, stringify it nicely
                            try {
                              displayValue = JSON.stringify(value, null, 2);
                            } catch (e) {
                              displayValue = String(value);
                            }
                          } else {
                            displayValue = String(value);
                          }
                          
                          return (
                            <div key={key} className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                              <span className="text-gray-600 font-medium break-words min-w-0 sm:min-w-[100px] sm:flex-shrink-0">{key}:</span>
                              <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Signature Preview */}
            {selectedAgreementRun.signatureUrl && (
              <div className="bg-white border border-gray-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1">Signature</h4>
                <img 
                  src={selectedAgreementRun.signatureUrl} 
                  alt="Signature"
                  className="max-w-full h-auto rounded border border-gray-300 bg-white"
                  style={{ maxHeight: '150px' }}
                />
              </div>
            )}

            {/* Signed PDF Link */}
            {selectedAgreementRun.signedPdfUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <h4 className="text-xs font-semibold text-gray-800 mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-blue-600" />
                  Signed Document
                </h4>
                <a
                  href={selectedAgreementRun.signedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  View Signed PDF
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full-Screen Reference Image Viewer */}
      {viewingReferenceImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[250] flex items-center justify-center"
          onClick={() => {
            setViewingReferenceImageUrl(null);
            setViewingReferenceImageUrls([]);
            setViewingReferenceImageIndex(0);
          }}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={() => {
                setViewingReferenceImageUrl(null);
                setViewingReferenceImageUrls([]);
                setViewingReferenceImageIndex(0);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            {viewingReferenceImageUrls.length > 1 && viewingReferenceImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingReferenceImageIndex - 1;
                  setViewingReferenceImageIndex(newIndex);
                  setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
                }}
                className="absolute left-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Previous image (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Next Button */}
            {viewingReferenceImageUrls.length > 1 && viewingReferenceImageIndex < viewingReferenceImageUrls.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingReferenceImageIndex + 1;
                  setViewingReferenceImageIndex(newIndex);
                  setViewingReferenceImageUrl(viewingReferenceImageUrls[newIndex]);
                }}
                className="absolute right-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Next image (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={viewingReferenceImageUrl}
              alt={`Reference image ${viewingReferenceImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter and Note */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
              {viewingReferenceImageUrls.length > 1 && (
                <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                  {viewingReferenceImageIndex + 1} / {viewingReferenceImageUrls.length}
                </div>
              )}
              {/* Show note if available */}
              {task.referenceImageMetadata && viewingReferenceImageUrl && (() => {
                const currentImageMetadata = task.referenceImageMetadata.find(
                  meta => meta.url === viewingReferenceImageUrl
                );
                if (currentImageMetadata?.note) {
                  return (
                    <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm max-w-md text-center">
                      <div className="font-medium mb-1">Note:</div>
                      <div>{currentImageMetadata.note}</div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewTaskModal;

